import React, { useEffect, useState } from "react";
import styled from "styled-components";
import AppContent from "../components/Content";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyAccessToken } from "../apis/auth";
import { JWT_KEY } from "../config/constant";
import { getUserAgent, isLoggedIn } from "../utils/utility";
import LottieComponent from "../components/Lottie";
import lottieLoading from "../assets/lottie/lottie-loading-purchase.json";
import { useModal } from "../utils/useModal";
import Modal from "../components/Modal";

const DefaultLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingDialogShown, setLoadingDialogShown] = useState(false);
  const { modalDataState, closeModal, openModal } = useModal();

  const sendTokenToNativeIOS = async () => {
    try {
      window.webkit?.messageHandlers?.getToken.postMessage({
        accessToken: window.localStorage.getItem(JWT_KEY),
        refreshToken: window.localStorage.getItem("brainy_refresh_token"),
      });
    } catch (e: any) {
      console.log(e);
      if (e.message === "토큰 재발급 실패")
        openModal({
          type: "login",
          message:
            "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
        });
      else openModal({ type: "guide", message: "오류가 발생했어요!" });
    }
  };

  const getAuth = async () => {
    if (
      pathname.includes("/mypage/terms") ||
      pathname.includes("introduction") ||
      pathname === "/splash"
    )
      return;

    if (!pathname.includes("/login") && !isLoggedIn()) {
      window.localStorage.clear();
      navigate(
        sessionStorage.getItem("splash") === "true" ? "/login" : "/splash"
      );
      return;
    }

    try {
      const { isSuccess } = await verifyAccessToken();

      if (isSuccess) {
        if (getUserAgent() === "apple") await sendTokenToNativeIOS();
      }
    } catch (e: any) {
      console.log(e);
      if (e.message === "토큰 재발급 실패")
        openModal({
          type: "login",
          message:
            "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
        });
      else openModal({ type: "guide", message: "오류가 발생했어요!" });

      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("INACTIVE")) navigate("/survey/age");
  }, []);

  useEffect(() => {
    const callApis = async () => {
      setIsLoading(true);
      setTimeout(() => {
        setLoadingDialogShown(true);
      }, 1000);
      // Path에 login이라는 문자가 포함되어 있지 않고, guide, introduction, splash가 아닌 경우
      if (
        !pathname.includes("login") &&
        pathname !== "/guide" &&
        pathname !== "/introduction" &&
        pathname !== "splash"
      )
        // 인증 체크 진행
        await getAuth();

      if (getUserAgent() === "apple") {
        try {
          window.webkit?.messageHandlers?.changeTab.postMessage({
            color:
              pathname === "/" ||
              pathname === "/splash" ||
              pathname === "/store"
                ? "main"
                : pathname.includes("study") || pathname.includes("mypage/edit")
                ? "grey"
                : "white",
          });
        } catch (e: any) {
          console.log(e);
          if (e.message === "토큰 재발급 실패")
            openModal({
              type: "login",
              message:
                "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
            });
          else openModal({ type: "guide", message: "오류가 발생했어요!" });
        }
      } else if (getUserAgent() === "android") {
        window.Android?.changeStatusBarColor(
          pathname === "/" || pathname === "/splash"
            ? "main"
            : pathname.includes("study") || pathname.includes("mypage/edit")
            ? "grey"
            : "white"
        );
      }
      setIsLoading(false);
    };

    // API 호출
    callApis();
  }, [pathname]);

  useEffect(() => {
    if (!isLoading) setLoadingDialogShown(false);
  }, [isLoading, loadingDialogShown]);

  return isLoading && loadingDialogShown ? (
    <LoadingStyled>
      <LottieComponent className="lottie" animationData={lottieLoading} loop />
      <div className="text">뉴런 연결중 ••• 🧠</div>
    </LoadingStyled>
  ) : !isLoading ||
    pathname.includes("login") ||
    pathname.includes("/mypage/terms") ||
    pathname.includes("/introduction") ||
    pathname === "/splash" ||
    pathname === "/guide" ? (
    <>
      <AppContent />
      {modalDataState.isOpen && (
        <Modal destroy={() => closeModal()}>
          <ModalContainerStyled>
            <div className="content-container">
              <div className="title">
                {modalDataState.type === "login"
                  ? "다시 로그인해주세요!"
                  : "안내 메시지"}
              </div>
              <div className="message">{modalDataState.message}</div>
              <div className="button-container">
                <button
                  type="button"
                  className="primary"
                  onClick={() => {
                    if (modalDataState.type === "login")
                      window.location.href = `${process.env.REACT_APP_URL}/login`;
                    closeModal();
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          </ModalContainerStyled>
        </Modal>
      )}
    </>
  ) : (
    <></>
  );
};

export default DefaultLayout;

const LoadingStyled = styled.main`
  max-width: 36rem;
  height: calc(100dvh - 4.6rem);
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 1.6rem;

  .lottie {
    width: 18.8rem;
    margin: 0 auto;
  }

  .text {
    font-size: 1.4rem;
    color: var(--color-grey-500);
    margin-bottom: 4.6rem;
  }
`;

const ModalContainerStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 32px 16px 24px;

  .content-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;

    .title {
      padding-bottom: 22px;
      font-weight: 600;
      font-size: 16px;
    }

    .message {
      padding-bottom: 22px;
      font-weight: 400;
      font-size: 14px;
      white-space: pre-line;
    }

    .primary {
      font-weight: 400;
    }
  }
`;
